import { ClaimType, ClaimsSearchRequest } from '@cms-max/medicare-api-sdk'
import { Duration, format, isBefore, parse, startOfMonth, endOfToday, sub } from 'date-fns'
import { TFunction, useTranslation } from 'react-i18next'
import { z } from 'zod'

const tConfig = {
  ns: 'claimsSearch'
}

export enum ClaimDateRange {
  LAST_30_DAYS = 'days30',
  LAST_6_MONTHS = 'months6',
  LAST_12_MONTHS = 'months12',
  LAST_24_MONTHS = 'months24',
  LAST_36_MONTHS = 'months36',
  CUSTOM = 'custom'
}

export type ClaimCustomDateRange = {
  start: string,
  end: string
}

const isValidDate = (val: string) => {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/
  if (!val || !regex.test(val)) return false

  const date = parse(val, 'MM/dd/yyyy', new Date())
  // Invalid dates would yield NaN, which is not equal to itself
  return date.getTime() === date.getTime() // eslint-disable-line no-self-compare
}

const isBeforeToday = (val: string) => {
  const date = parse(val, 'MM/dd/yyyy', new Date())
  return isBefore(date, endOfToday())
}

const ClaimTypeAny = ''
export const createSchema = (t: TFunction) => {
  return z.object({
    claimNumber: z.string().regex(/^$|^[a-zA-Z0-9]{7,}$/, t('filterValidation.claimNumber', tConfig)).optional(),
    customDateRange: z.object({
      start: z.string({ required_error: t('filterValidation.customDateRange.start.required', tConfig) })
        .refine(isValidDate, { message: t('filterValidation.customDateRange.start.invalid', tConfig) })
        .refine(isBeforeToday, { message: t('filterValidation.customDateRange.start.future', tConfig) }),
      end: z.string({ required_error: t('filterValidation.customDateRange.end.required', tConfig) })
        .refine(isValidDate, { message: t('filterValidation.customDateRange.end.invalid', tConfig) })
        .refine(isBeforeToday, { message: t('filterValidation.customDateRange.end.future', tConfig) })
    }).optional()
      .refine((range) => {
        if (range?.start && range?.end) {
          return isBefore(parse(range.start, 'MM/dd/yyyy', new Date()), parse(range.end, 'MM/dd/yyyy', new Date()))
        }
        return true
      }, { message: t('filterValidation.customDateRange.start.relative', tConfig), path: ['start'] }),
    claimType: z.enum([
      ClaimType.Inpatient,
      ClaimType.Outpatient,
      ClaimType.HomeHealth,
      ClaimType.Hospice,
      ClaimType.PartB,
      ClaimType.Dme,
      ClaimTypeAny
    ]).optional(),
    dateRange: z.nativeEnum(ClaimDateRange)
  })
}

export type ClaimsFiltersForm = z.infer<ReturnType<typeof createSchema>>

export const defaultClaimsFiltersForm: ClaimsFiltersForm = {
  dateRange: ClaimDateRange.LAST_30_DAYS,
  customDateRange: undefined,
  claimType: ClaimTypeAny
}

export enum ClaimSortType {
  CLAIM_TYPE_ASC = 'claim-type-asc',
  CLAIM_TYPE_DESC = 'claim-type-desc',
  MEDICARE_APPROVED_AMOUNT_ASC = 'medicare-approved-amt-asc',
  MEDICARE_APPROVED_AMOUNT_DESC = 'medicare-approved-amt-desc',
  SERVICE_DATE_ASC = 'oldest-service-date',
  SERVICE_DATE_DESC = 'newest-service-date'
}

export interface ClaimsSortForm {
  sortType: ClaimSortType
}

export const useClaimTypeMapping = () => {
  const { t } = useTranslation('claimsSearch')

  return {
    [ClaimType.Inpatient]: t('filterClaimTypeInpatient'),
    [ClaimType.Outpatient]: t('filterClaimTypeOutpatient'),
    [ClaimType.HomeHealth]: t('filterClaimTypeHomeHealth'),
    [ClaimType.Hospice]: t('filterClaimTypeHospice'),
    [ClaimType.PartB]: t('filterClaimTypePartB'),
    [ClaimType.Dme]: t('filterClaimTypeDME')
  }
}

export const useClaimTypeOptions = () => {
  const mapping = useClaimTypeMapping()

  return Object.entries(mapping).map(([key, value]) => ({
    value: key,
    label: value
  }))
}

export const useClaimDateRangeMapping = () => {
  const { t } = useTranslation('claimsSearch')

  return {
    [ClaimDateRange.LAST_30_DAYS]: t('filterClaimRange1Month'),
    [ClaimDateRange.LAST_6_MONTHS]: t('filterClaimRange6Months'),
    [ClaimDateRange.LAST_12_MONTHS]: t('filterClaimRange12Months'),
    [ClaimDateRange.LAST_24_MONTHS]: t('filterClaimRange24Months'),
    [ClaimDateRange.LAST_36_MONTHS]: t('filterClaimRange36Months')
  }
}

export const useClaimDateRangeOptions = () => {
  const mapping = useClaimTypeMapping()

  return Object.entries(mapping).map(([key, value]) => ({
    value: key,
    label: value
  }))
}

const calculateStartDate = (dateRange: ClaimDateRange) => {
  const dateStartMapping = {
    [ClaimDateRange.LAST_30_DAYS]: sub(new Date(), { days: 30 }),
    [ClaimDateRange.LAST_6_MONTHS]: startOfMonth(sub(new Date(), { months: 6 })),
    [ClaimDateRange.LAST_12_MONTHS]: startOfMonth(sub(new Date(), { months: 12 })),
    [ClaimDateRange.LAST_24_MONTHS]: startOfMonth(sub(new Date(), { months: 24 })),
    [ClaimDateRange.LAST_36_MONTHS]: startOfMonth(sub(new Date(), { months: 36 }))
  }
  // @ts-ignore - TS complaining about ClaimDateRange.CUSTOM, which isn't currently used
  return dateStartMapping[dateRange]
}

export const useFormattedDateRange = (dateRange: ClaimDateRange, customDateRange?: ClaimCustomDateRange) => {
  const { t } = useTranslation('claimsSearch')

  if (dateRange === ClaimDateRange.CUSTOM) {
    const startDate = customDateRange?.start
    const endDate = customDateRange?.end
    return t('currentSearch.dateRangeText', { startDate, endDate })
  }

  const startDate = format(calculateStartDate(dateRange), 'MM/dd/yyyy')
  const endDate = format(new Date(), 'MM/dd/yyyy')
  return t('currentSearch.dateRangeText', { startDate, endDate })
}

function beginAtMonthStart (dateRange: ClaimDateRange) {
  return [ClaimDateRange.LAST_6_MONTHS, ClaimDateRange.LAST_12_MONTHS, ClaimDateRange.LAST_24_MONTHS, ClaimDateRange.LAST_36_MONTHS].includes(dateRange)
}

export function getClaimsSearchRequest ({ dateRange, claimNumber, claimType, customDateRange }: ClaimsFiltersForm): ClaimsSearchRequest {
  const duration: Duration = {}
  switch (dateRange) {
    case ClaimDateRange.LAST_30_DAYS:
      duration.days = 30
      break
    case ClaimDateRange.LAST_6_MONTHS:
      duration.months = 6
      break
    case ClaimDateRange.LAST_12_MONTHS:
      duration.months = 12
      break
    case ClaimDateRange.LAST_24_MONTHS:
      duration.months = 24
      break
    case ClaimDateRange.LAST_36_MONTHS:
      duration.months = 36
      break
    case ClaimDateRange.CUSTOM:
      break
    default:
      throw new Error(`Unsupported date range: ${dateRange}`)
  }
  const presetEndDate = new Date(Date.now())
  const presetStartDate = beginAtMonthStart(dateRange) ? startOfMonth(sub(presetEndDate, duration)) : sub(presetEndDate, duration)
  const customEndDate = customDateRange?.end ? parse(customDateRange?.end, 'MM/dd/yyyy', new Date()) : undefined
  const customStartDate = customDateRange?.start ? parse(customDateRange?.start, 'MM/dd/yyyy', new Date()) : undefined

  const req: ClaimsSearchRequest = {
    startDateMs: (customStartDate ?? presetStartDate).getTime(),
    endDateMs: (customEndDate ?? presetEndDate).getTime()
  }
  if (claimNumber) {
    req.claimNumber = claimNumber
  }
  if (claimType) {
    req.claimType = claimType
  }
  return req
}
